import { CSSProperties, PropsWithChildren, useMemo } from 'react';
import { SortingArrowDownIcon, SortingArrowInActiveIcon, SortingArrowUpIcon } from '~/images/icons';
import { Colors } from '~/themes/colors';
import { Row } from '../Row';

type TVariant = 'default' | 'primary';

export type TSortValue = {
  direction: 'ASC' | 'DESC' | string;
  field: string;
};

interface ISortingArrowProps {
  name: string;
  sorting: TSortValue;
  style?: CSSProperties;
  variant?: TVariant;
  onChange?(value: TSortValue | undefined): void;
}

const VariantColors: Partial<Record<TVariant, { Active: string; InActive: string }>> = {
  primary: {
    Active: Colors.primary_500,
    InActive: Colors.primary_500,
  },
};

export const SortingArrow = ({
  children,
  name,
  sorting,
  style,
  variant = 'default',
  onChange,
}: PropsWithChildren<ISortingArrowProps>) => {
  const ArrowComponent = useMemo(() => {
    const isInactive = name !== sorting.field;

    if (isInactive) {
      return <SortingArrowInActiveIcon color={VariantColors[variant]?.InActive} size={10} />;
    }

    if (sorting.direction === 'ASC') {
      return <SortingArrowUpIcon color={VariantColors[variant]?.Active} size={10} />;
    }

    return <SortingArrowDownIcon color={VariantColors[variant]?.Active} size={10} />;
  }, [name, sorting.direction, sorting.field, variant]);

  const handleSortChanges = () => {
    const isActive = sorting.field === name;

    if (!isActive) {
      // INACTIVE → DESC (first click)
      onChange?.({
        field: name,
        direction: 'DESC',
      });
    } else if (sorting.direction === 'DESC') {
      // DESC → ASC (second click)
      onChange?.({
        field: name,
        direction: 'ASC',
      });
    } else {
      // ASC → INACTIVE (third click)
      onChange?.(undefined);
    }
  };

  return (
    <Row
      alignItems="center"
      cursor="pointer"
      gap={4}
      role="button"
      style={{
        ...style,
        justifyContent: style?.justifyContent || 'center',
      }}
      onClick={handleSortChanges}
    >
      {children}
      {ArrowComponent}
    </Row>
  );
};
